import { InputAdornment, TextField } from "@mui/material";
import Grid from "@mui/material/Grid2";
import cep from "cep-promise";
import { Formik } from "formik";
import React, { useRef } from "react";
import InputMaskWrapper from "@/components/atoms/InputMaskWrapper";

import FormField from "@/components/atoms/FormField";
import MDBox from "@/components/atoms/MDBox";
import { MDButton } from "@/components/atoms/MDButton";
import MDTypography from "@/components/atoms/MDTypography";
import {
  buildContactAddressFormSchema,
  buildDefaultAddressFormData,
  normalizeAddressFormData,
} from "@/components/screens/ContactFormPage/form-data";
import { AddressFormData } from "@/components/screens/ContactFormPage/types";
import { CEP_LENGHT, STATE_UF_TRANSLATIONS } from "@/constants/documents.constants";
import SWR_KEYS from "@/constants/swr-keys";
import useAuth from "@/contexts/auth-context";
import { removeNonIntegersFromString } from "@/helpers/string-helpers";
import useCreateAdditionalAddress from "@/hooks/requests/contact/AdditionalContactAddresses/use-create-address";
import useUpdateAdditionalAddress from "@/hooks/requests/contact/AdditionalContactAddresses/use-update-address";
import useLoading from "@/hooks/use-loading";
import useMutate from "@/hooks/use-mutate";
import IContact, { IAddress, IAddressInput } from "@/interfaces/contact.interface";
import { ContactAddressMessages } from "@/messages/contacts/address-section.messages";

type Props = {
  messages: ContactAddressMessages;
  address: IAddress | null;
  contact: IContact | null;
  isEditing: boolean;
  onClose: () => void;
};

function AddressSectionDialog({ messages, contact, isEditing, onClose, address }: Props) {
  const { update } = useUpdateAdditionalAddress();
  const { create } = useCreateAdditionalAddress();
  const { mutate } = useMutate();
  const { user } = useAuth();
  const _id = Number(address?.id) || 0;
  const { startLoading, endLoading, isLoading } = useLoading({ loadingIconColor: "primary" });

  const {
    startLoading: startLoadingCEP,
    endLoading: endLoadingCEP,
    isLoading: isLoadingCEP,
    loadingIcon: loadingIconCEP,
  } = useLoading({ loadingIconColor: "primary" });

  const onSuccess = () => {
    mutate(SWR_KEYS.getContactById);
    onClose();
  };

  const onError = () => endLoading();

  const handleUpdate = async (formData: IAddressInput) => {
    startLoading();

    const successMessage = messages.contactUpdateSuccess;
    const errorMessage = messages.contactUpdateError;

    await update({ _id, formData, successMessage, errorMessage, onSuccess, onError });
  };

  const handleCreate = async (formData: IAddressInput) => {
    startLoading();

    const successMessage = messages.contactCreateSuccess;
    const errorMessage = messages.contactCreateError;

    await create({ successMessage, errorMessage, formData, onSuccess, onError });
  };

  const handleSubmitForm = async (_formData: AddressFormData) => {
    if (!user) return;

    const formData = normalizeAddressFormData(_formData);

    if (isEditing) handleUpdate(formData);
    if (!isEditing) handleCreate(formData);
  };

  const addressNumberRef = useRef<HTMLInputElement>(null);

  return (
    <Formik
      initialValues={buildDefaultAddressFormData(address, contact)}
      validationSchema={buildContactAddressFormSchema(messages)}
      validateOnChange
      onSubmit={handleSubmitForm}
    >
      {(formik) => {
        const { values, handleChange, errors, setFieldValue, setFieldError } = formik;

        const clearData = () => {
          setFieldValue("addressState", "");
          setFieldValue("addressStateUf", "");
          setFieldValue("addressCity", "");
          setFieldValue("addressDistrict", "");
          setFieldValue("address", "");
        };

        const handleCEPChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
          const {
            target: { value },
          } = e;
          const formattedValue = removeNonIntegersFromString(value);
          setFieldError("addressZipCode", undefined);
          setFieldValue("addressZipCode", formattedValue);

          const isCEPCompleted = formattedValue.length === CEP_LENGHT;
          if (!isCEPCompleted) clearData();
          if (isCEPCompleted) {
            startLoadingCEP();

            try {
              const cepData = await cep(formattedValue);
              const addressState = STATE_UF_TRANSLATIONS[cepData.state];
              setFieldValue("addressState", addressState);
              setFieldValue("addressStateUf", cepData.state);
              setFieldValue("addressCity", cepData.city);
              setFieldValue("addressDistrict", cepData.neighborhood);
              setFieldValue("address", cepData.street);

              setTimeout(() => {
                addressNumberRef.current?.focus();
              }, 0);
            } catch (err) {
              setFieldError("addressZipCode", messages.cepNotFoundError);
              clearData();
            }
            endLoadingCEP();
          }
        };

        return (
          <>
            <MDBox p={3}>
              <MDBox display="flex" justifyContent="space-between">
                <MDTypography variant="h5" fontWeight="bold">
                  {messages.contactAddressTitle}
                </MDTypography>
              </MDBox>

              <MDBox mt={1}>
                <Grid container spacing={3}>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <FormField
                      variant="standard"
                      label={messages.addressNameLabel}
                      name="addressName"
                      value={values.addressName}
                      onChange={handleChange}
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      error={!!errors.addressName}
                      required
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <InputMaskWrapper
                      mask={"99999-999"}
                      value={values.addressZipCode}
                      name="addressZipCode"
                      onChange={(e) => handleCEPChange(e)}
                      variant="standard"
                      label={messages.addressZipCodeLabel}
                      fullWidth
                      error={!!errors.addressZipCode}
                      helperText={errors.addressZipCode}
                      required
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <FormField
                      variant="standard"
                      name="address"
                      label={messages.addressLabel}
                      value={values.address}
                      onChange={handleChange}
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      error={!!errors.address}
                      required
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <FormField
                      variant="standard"
                      name="addressNumber"
                      label={messages.addressNumberLabel}
                      value={values.addressNumber}
                      onChange={handleChange}
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      error={!!errors.addressNumber}
                      inputRef={addressNumberRef}
                      required
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <FormField
                      variant="standard"
                      name="addressCompl"
                      label={messages.addressComplLabel}
                      value={values.addressCompl}
                      onChange={handleChange}
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      error={!!errors.addressCompl}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <FormField
                      variant="standard"
                      name="addressDistrict"
                      label={messages.addressDistrictLabel}
                      value={values.addressDistrict}
                      onChange={handleChange}
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      error={!!errors.addressDistrict}
                      required
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <FormField
                      variant="standard"
                      name="addressCity"
                      label={messages.addressCityLabel}
                      value={values.addressCity}
                      onChange={handleChange}
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      error={!!errors.addressCity}
                      required
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <FormField
                      variant="standard"
                      label={messages.addressStateLabel}
                      name="addressStateUf"
                      value={values.addressStateUf}
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      error={!!errors.addressStateUf}
                      required
                    />
                  </Grid>

                  <Grid size={{ xs: 12, sm: 4 }}>
                    <FormField
                      variant="standard"
                      name="addressCountry"
                      label={messages.addressCountryLabel}
                      value={values.addressCountry}
                      onChange={handleChange}
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      error={!!errors.addressCountry}
                      required
                    />
                  </Grid>

                  <MDBox mt={2} display="flex" width={"100%"} justifyContent="flex-end" gap={2}>
                    <MDButton
                      variant="gradient"
                      color="light"
                      onClick={onClose}
                      isLoading={isLoading}
                    >
                      {messages.cancelButton}
                    </MDButton>

                    <MDButton
                      variant="gradient"
                      color="dark"
                      onClick={formik.submitForm}
                      isLoading={isLoading}
                    >
                      {messages.updateButton}
                    </MDButton>
                  </MDBox>
                </Grid>
              </MDBox>
            </MDBox>
          </>
        );
      }}
    </Formik>
  );
}
export default AddressSectionDialog;
